import React, { useState, useRef } from 'react'
import { ExternalLink, FileCheck, Pin, PinOff, Trash2, X as XIcon } from 'lucide-react'
import { ChevronRightIcon } from '../../icons/ChevronRightIcon'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '../../ui/context-menu'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../ui/alert-dialog'
import { usePlatform } from '../../../hooks/usePlatform'
import { useSidebarCollapsed } from './SidebarContext'
import { SidebarItemProps } from './SidebarItem.types'
import './SidebarItem.scss'

export const SidebarItem: React.FC<SidebarItemProps> = ({
  icon: Icon,
  label,
  badge,
  isActive = false,
  expandable = false,
  defaultExpanded = false,
  indent = false,
  path,
  hasOpenTab = false,
  isPreview = false,
  isPinned = false,
  hasSubItems = false,
  isSubMenuExpanded = false,
  hasActiveSubItem = false,
  onToggleSubMenu,
  onCloseTab,
  onUnpin,
  onPinTab,
  onOpenNewTab,
  onPromote,
  onDelete,
  onClick,
  onDoubleClick,
  children,
  trailing,
}) => {
  const { t } = useTranslation()
  const { isMac } = usePlatform()
  const isCollapsed = useSidebarCollapsed()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const hasToggledRef = useRef(false)

  const handleClick = (e: React.MouseEvent) => {
    if (expandable && children) {
      hasToggledRef.current = true
      setIsExpanded(!isExpanded)
    } else if (onClick) {
      onClick(e)
    }
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!expandable && onDoubleClick) {
      onDoubleClick(e)
    }
  }

  const handleCloseTab = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCloseTab?.()
  }

  const handleOpenNewTab = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpenNewTab?.()
  }

  // Only show context menu for non-expandable items (leaf items with a path)
  const hasContextMenu = !expandable && !!path

  const itemContent = (
    <div
      className={`sidebar-item ${isActive && !hasActiveSubItem ? 'sidebar-item--active' : ''} ${hasActiveSubItem ? 'sidebar-item--has-active-sub' : ''} ${hasSubItems ? 'sidebar-item--has-sub-items' : ''} ${indent ? 'sidebar-item--indent' : ''} ${hasOpenTab ? 'sidebar-item--has-tab' : ''} ${isPreview ? 'sidebar-item--preview' : ''} ${isPinned ? 'sidebar-item--pinned' : ''}`}
      data-path={path}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {expandable && children && (
        <span className={`sidebar-item__chevron ${isExpanded ? 'sidebar-item__chevron--expanded' : ''}`}>
          <ChevronRightIcon size={14} />
        </span>
      )}

      {Icon && (
        <span className="sidebar-item__icon">
          <Icon size={16} />
        </span>
      )}

      {hasSubItems && onToggleSubMenu && (
        <button
          className={`sidebar-item__sub-chevron ${isSubMenuExpanded ? 'sidebar-item__sub-chevron--expanded' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleSubMenu() }}
          aria-label={isSubMenuExpanded ? t('sidebar.collapseSubMenu') : t('sidebar.expandSubMenu')}
        >
          <ChevronRightIcon size={16} />
        </button>
      )}

      <span className="sidebar-item__label">{label}</span>

      {isPinned && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="sidebar-item__pin-button"
              onClick={(e) => { e.stopPropagation(); onUnpin?.() }}
              aria-label={t('sidebar.unpinTab')}
            >
              <Pin size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('sidebar.unpinTab')}</TooltipContent>
        </Tooltip>
      )}

      {badge !== undefined && badge > 0 && (
        <span className="sidebar-item__badge">{badge > 99 ? '99+' : badge}</span>
      )}

      {trailing && (
        <span className="sidebar-item__trailing">
          {trailing}
        </span>
      )}

      {hasOpenTab && !isPinned && onCloseTab && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="sidebar-item__close-tab"
              onClick={handleCloseTab}
              aria-label={t('sidebar.closeTab', { shortcut: isMac ? '⌘W' : 'Ctrl+W' })}
            >
              <XIcon size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('sidebar.closeTab', { shortcut: isMac ? '⌘W' : 'Ctrl+W' })}</TooltipContent>
        </Tooltip>
      )}

      {!hasOpenTab && onOpenNewTab && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="sidebar-item__open-tab"
              onClick={handleOpenNewTab}
              aria-label={t('sidebar.openInNewTab', { shortcut: isMac ? '⌘+Click' : 'Ctrl+Click' })}
            >
              <ExternalLink size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('sidebar.openInNewTab', { shortcut: isMac ? '⌘+Click' : 'Ctrl+Click' })}</TooltipContent>
        </Tooltip>
      )}

    </div>
  )

  const contextMenuContent = hasContextMenu ? (
    <ContextMenuContent>
      {!isActive && !hasOpenTab && (
        <ContextMenuItem onSelect={() => onOpenNewTab?.()}>
          <ExternalLink size={14} className="context-menu-content__item-icon" />
          {t('sidebar.contextMenu.openInNewTab')}
        </ContextMenuItem>
      )}
      {hasOpenTab && (
        <>
          {isPreview && onPromote && (
            <ContextMenuItem onSelect={() => onPromote()}>
              <FileCheck size={14} className="context-menu-content__item-icon" />
              {t('sidebar.contextMenu.keepTab')}
            </ContextMenuItem>
          )}
          <ContextMenuItem onSelect={() => isPinned ? onUnpin?.() : onPinTab?.()}>
            {isPinned
              ? <PinOff size={14} className="context-menu-content__item-icon" />
              : <Pin size={14} className="context-menu-content__item-icon" />
            }
            {isPinned ? t('sidebar.contextMenu.unpinTab') : t('sidebar.contextMenu.pinTab')}
            <ContextMenuShortcut>P</ContextMenuShortcut>
          </ContextMenuItem>
          {onCloseTab && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={() => onCloseTab()}>
                <XIcon size={14} className="context-menu-content__item-icon" />
                {t('sidebar.contextMenu.closeTab')}
                <ContextMenuShortcut>{isMac ? '⌘W' : 'Ctrl+W'}</ContextMenuShortcut>
              </ContextMenuItem>
            </>
          )}
        </>
      )}
      {onDelete && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            className="context-menu-content__item--danger"
            onSelect={() => setDeleteDialogOpen(true)}
          >
            <Trash2 size={14} className="context-menu-content__item-icon" />
            {t('sidebar.contextMenu.deleteProject')}
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  ) : null

  const renderedItem = isCollapsed ? (
    hasContextMenu ? (
      <Tooltip>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <TooltipTrigger asChild>
              {itemContent}
            </TooltipTrigger>
          </ContextMenuTrigger>
          {contextMenuContent}
        </ContextMenu>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          {itemContent}
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    )
  ) : (
    hasContextMenu ? (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {itemContent}
        </ContextMenuTrigger>
        {contextMenuContent}
      </ContextMenu>
    ) : itemContent
  )

  return (
    <>
      {renderedItem}

      {expandable && isExpanded && children && (
        <div className={`sidebar-item__children ${hasToggledRef.current ? 'sidebar-item__children--animate' : ''}`}>
          {children}
        </div>
      )}

      {onDelete && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('project.confirmDelete.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('project.confirmDelete.description', { name: label })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('project.confirmDelete.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="alert-dialog-action--danger"
                onClick={() => onDelete()}
              >
                {t('project.confirmDelete.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}
